import { AxiosResponse } from "axios";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";

import * as actions from "./actions";
import * as actionTypes from "./actionTypes";
import * as types from "./types";
import * as services from "./service";
import * as alert from "../alert/actions";

/*
  Worker Sagas
*/

function* fetchLoaderListSaga(action: types.FetchLoadersReq) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IDataLoaderList | types.IApiError> = yield call(services.getDataLoaders, action.params);
        if (response.status === 200) {
            yield put(
                actions.fetchLoadersSuccess({
                    loaders: response.data as types.IDataLoaderList,
                })
            );

        }

        //Show IApiError
        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
        }

    } catch (e: any) {
        yield put(
            actions.fetchLoadersFailure({
                error: e,
            })
        );
    }
}

function* fetchLoaderDetailsSaga(action: types.FetchLoaderDetailsReq) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IFetchDataLoaderDetails | types.IApiError> = yield call(services.getLoaderDetails, action.params);
        if (response.status === 200) {
            yield put(
                actions.fetchLoaderDetailsSuccess({
                    loaderDetails: response.data as types.IFetchDataLoaderDetails,
                })
            );

        }

        //Show IApiError
        if (response.status !== 200) {

            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
        }

    } catch (e: any) {
        yield put(
            actions.fetchLoaderDetailsFailure({
                error: e,
            })
        );
    }
}

function* deleteLoaderSaga(action: types.DeleteLoaderRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IDeleteLoaderSuccess[] | types.IApiError> = yield call(services.deleteLoader, action.payload);
        if (response.status === 200) {
            let dataPayload = response.data as types.IDeleteLoaderSuccess[];
            yield put(
                actions.deleteLoaderSuccess({
                    deleteStatus: dataPayload
                })
            );
            if (dataPayload[0].isDeleted === true) yield put(alert.enqueueSnackbar({ message: `The Loader was successfully deleted, Timestamp: ${timestamp}`, options: { variant: "success" } }));
            else yield put(alert.enqueueSnackbar({ message: `The Loader could not be deleted, Timestamp: ${timestamp}`, options: { variant: "error" } }));

        }

        //Show IApiError
        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            if (apiError.errorType === 'Not Executed') {
                yield put(alert.enqueueSnackbar({ message: `The Loader was successfully deleted, Timestamp: ${timestamp}`, options: { variant: "success" } }));
            } else {
                let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}`;
                yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
            }
        }

    } catch (e: any) {
        yield put(
            actions.deleteLoaderFailure({
                error: e,
            })
        );
    }
}

function* postDataLoaderSaga({ payload }: types.PostDataLoaderRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IPostDataLoaderSuccess | types.IApiError> = yield call(services.postDataLoader, payload);
        if (response.status === 201) {
            let dataPayload = response.data as types.IPostDataLoaderSuccess;
            yield put(
                actions.postDataLoaderSuccess({
                    dataLoader: dataPayload,
                })
            );
            let successMsg = `The data loader was successfully created, with loader ID: ${dataPayload.batchCode}, Timestamp: ${timestamp}.`;
            yield put(alert.enqueueSnackbar({ message: successMsg, options: { variant: "success" } }));
        }

        if (response.status !== 201) {
            let apiError = response.data as types.IApiError;
            let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}.`;
            yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));
            yield put(
                actions.postDataLoaderFailure({
                    error: apiError,
                })
            );
        }
    } catch (e: any) {
        yield put(
            actions.postDataLoaderFailure({
                error: e,
            })
        );
    }
}



function* putDataLoaderSaga({ batchCode, payload }: types.PutDataLoaderRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IPutDataLoaderSuccess | types.IApiError> = yield call(services.putDataLoader, batchCode, payload);

        if (response.status === 200) {
            let dataPayload = response.data as types.IPutDataLoaderSuccess;
            yield put(
                actions.putDataLoaderSuccess({
                    dataLoader: dataPayload,
                })
            );
            let successMsg = `The data loader was successfully edited. loader ID: ${batchCode}, Timestamp: ${timestamp}.`;
            yield put(alert.enqueueSnackbar({ message: successMsg, options: { variant: "success" } }));
        }

        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}.`;
            yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));

        }
    } catch (e: any) {
        yield put(
            actions.putDataLoaderFailure({
                error: e,
            })
        );
    }
}

function* fetchLoaderStatusSaga(action: types.FetchLoaderStatusReq) {
    try {
        const response: AxiosResponse<types.ISingleLoaderStatus[]> = yield call(services.getLoaderStatus, action.params);
        yield put(
            actions.fetchLoaderStatusSuccess({
                loaderStatus: response.data as types.ISingleLoaderStatus[]
            })
        );
    } catch (e: any) {
        yield put(
            actions.fetchLoaderStatusFailure({
                error: e,
            })
        );
    }
}


/*
  Watcher saga - intercept the action fired from view
  Starts worker saga on latest dispatched `FETCH_ACCESSCONTROL_REQUEST` action.
  Allows concurrent increments.
*/
function* loaderListSaga() {
    yield takeLatest(actionTypes.FETCH_LOADERS_LIST_REQUEST, fetchLoaderListSaga);
}

function* loaderDetailsSaga() {
    yield takeEvery(actionTypes.FETCH_LOADER_REQUEST, fetchLoaderDetailsSaga);
}

function* deleteDataLoaderSaga() {
    yield takeEvery(actionTypes.DELETE_LOADER_REQUEST, deleteLoaderSaga);
}

function* createDataLoaderSaga() {
    yield takeLatest(actionTypes.POST_DATALOADER_REQUEST, postDataLoaderSaga);
}

function* editDataLoaderSaga() {
    yield takeEvery(actionTypes.PUT_DATALOADER_REQUEST, putDataLoaderSaga);
}

function* getLoaderStatusSaga() {
    yield takeEvery(actionTypes.FETCH_LOADER_STATUS_REQUEST, fetchLoaderStatusSaga);
}


function* dataLoaderSaga() {
    yield all([
        loaderListSaga(),
        loaderDetailsSaga(),
        createDataLoaderSaga(),
        getLoaderStatusSaga(),
        deleteDataLoaderSaga(),
        editDataLoaderSaga(),
    ])
};

export default dataLoaderSaga;
